/**
 * VisibleTextField - the canonical single-line text input that says, on the
 * field and before the save, that a value the server would refuse is only
 * invisible characters (TASK-VTI-TEXT-FIELD CHG-002 and CHG-009;
 * SPEC-FRONTEND-RULE-097 (2), SPEC-FRONTEND-RULE-035, D-378).
 *
 * ## Why this component exists (RULE-035 (a) reuse justification)
 *
 * The inventory was searched before it was written and nothing covered the
 * role. `ExpandableTextField` is the canonical MULTILINE long-text editor and
 * is EXTENDED for this rule with an opt-in `visibleTextCheck` prop rather than
 * forked (RULE-033 step 2); `NumberField` is numeric only; `MetadataSchemaForm`
 * is schema-driven and builds its own controls; `StyledSelect` and
 * `StyledAutocomplete` are choices, not free text. The audit (U12) measured 285
 * raw MUI `TextField` usages across 108 files with no shared wrapper anywhere,
 * so new code had nothing obvious to reach for. This is the RULE-033 step 5
 * case: a genuinely new role, with its own DoD-bearing TASK.
 *
 * Planned consumers, each wired by its own sibling TASK (>=2 as RULE-033 and
 * RULE-035 (a) require):
 *
 * - `web/src/pages/admin/SystemSettingsPage.tsx` (TASK-VTI-FORMS-ADMIN);
 * - `web/src/pages/tenant/agent/components/ScheduleConfigForm.tsx`, the
 *   schedule name (TASK-VTI-FORMS-TENANT);
 * - `web/src/pages/tenant/agent/AgentSettingsGeneral.tsx`, the agent name and
 *   description (TASK-VTI-FORMS-TENANT).
 *
 * This TASK wires no consumer itself, and a COMPONENT-GATE ratchet owned by
 * TASK-VTI-GATE is what will refuse a NEW raw `TextField` on an inventoried
 * form.
 *
 * ## What it is, and what it is not
 *
 * It is a THIN composition over MUI's `TextField` (RULE-033 step 3), not a
 * fork: every `TextFieldProps` prop passes through unchanged, so a caller
 * swapping `TextField` for `VisibleTextField` changes nothing but the added
 * check. It adds no styling of its own, so it inherits the theme's tokens
 * wholesale and carries zero inline hex and zero magic px (RULE-034).
 *
 * It does NOT rewrite the value. There is no trim, no fill-in and no
 * normalisation anywhere in this file: what the user typed reaches the
 * caller's `onChange` byte for byte (RULE-097 (3)). It also never echoes or
 * logs the value - the message names neither the value nor any internal key,
 * and this module writes nothing to console or telemetry.
 *
 * Server refusals are NOT rendered here: a 400/422 from RULE-INTEG-031 renders
 * at the form per SPEC-FRONTEND-RULE-088 (the error code only). The field
 * message below is only ever the client check.
 *
 * ## States (RULE-035 (c), UX-GATE RULE-042)
 *
 * An input renders no list, fetches nothing and has no data states, so
 * `loading`, `empty` and `no-results` are NOT APPLICABLE to it and are
 * deliberately absent. The states that do apply are all implemented and
 * tested: READY (no message), ERROR (the blank message, with the error
 * palette, `aria-invalid` and `aria-describedby`), DISABLED (no message at all
 * - a disabled field is not something the user can fix, and the save gate is
 * the form's), and a visible FOCUS ring, which is MUI's themed default and is
 * never removed here.
 *
 * ## Responsive, and the Hungarian (RULE-035 (f), RULE-047)
 *
 * The wrapper sets no width, no min-width and no fixed px of its own, so the
 * field is exactly what its container gives it at every SPA breakpoint, and
 * `fullWidth` stays the caller's decision. The message renders in MUI's
 * `FormHelperText`, which wraps: it is never set to `nowrap` and never
 * truncated, which matters because the Hungarian string is the longer of the
 * two and is the locale the field is reviewed in.
 *
 * ## The manual accessibility pass (RULE-046) - OWED at review, not claimed here
 *
 * Automated checks verify part of this and no more, so the reviewer ticks the
 * manual keyboard + screen-reader (NVDA or VoiceOver) pass in the PR, checking
 * exactly three things: Tab reaches the field and the focus ring is visible;
 * returning focus to a flagged field announces the shared message as the
 * field's DESCRIPTION (it is wired through `aria-describedby`); and the field
 * is announced as invalid (`aria-invalid="true"`). The unit tests assert the
 * wiring those three depend on; they do not discharge the pass.
 *
 * ## When the message appears (RULE-097 (2) reconciled with RULE-043)
 *
 * On blur, on a submit attempt, or immediately for a value the server already
 * stored blank (`initialFromServer`) - never while the user is still typing
 * into a clean field. The `blank` state, on the other hand, is reported from
 * the first render through `onBlankChange`, so the form's save can be disabled
 * before the user has touched anything. The user therefore never meets only an
 * error code after pressing save.
 *
 * @module components/shared/VisibleTextField/VisibleTextField
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import type { TextFieldProps } from "@mui/material/TextField";

import { useVisibleTextError } from "./useVisibleTextError";

/**
 * Every MUI `TextFieldProps` prop, plus the three this wrapper adds.
 *
 * `value` is narrowed from MUI's `unknown` to `string`, because the blank test
 * is defined on text. Everything else is MUI's surface untouched.
 */
export interface VisibleTextFieldProps extends Omit<TextFieldProps, "value"> {
  /**
   * The field's current value. Read for the blank test and passed to MUI
   * unchanged; it is never trimmed, normalised or rewritten.
   */
  value?: string;
  /**
   * Called with the blank state once on mount and then on every TRANSITION, so
   * a form can lift it into its save-disabled condition without running a
   * second blank test of its own.
   *
   * It reports the STATE, not the message: it fires for a blank value the user
   * has not blurred yet, which is exactly the case a save gate must already
   * know about.
   */
  onBlankChange?: (blank: boolean) => void;
  /**
   * The value arrived from the server already blank, so flag it on load
   * instead of waiting for a blur (RULE-097 (2), last sentence). The user did
   * not type it and would otherwise have to guess why the save is disabled.
   *
   * @defaultValue false
   */
  initialFromServer?: boolean;
  /**
   * The surrounding form attempted a submit, which reveals the message on this
   * field even if it was never focused (RULE-043's "on submit" leg).
   *
   * @defaultValue false
   */
  submitAttempted?: boolean;
}

/**
 * A MUI `TextField` that says a non-empty blank value is blank, on the field,
 * before the save.
 *
 * @example A form field with the save gated on the same state
 * ```tsx
 * const [name, setName] = useState(loaded.name);
 * const [nameBlank, setNameBlank] = useState(false);
 *
 * <VisibleTextField
 *   label={t`Agent name`}
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 *   onBlankChange={setNameBlank}
 *   initialFromServer
 * />
 * <SaveButton disabled={nameBlank} onClick={save} />
 * ```
 */
const VisibleTextField = React.forwardRef<HTMLDivElement, VisibleTextFieldProps>(
  function VisibleTextField(
    {
      value = "",
      onBlankChange,
      initialFromServer = false,
      submitAttempted = false,
      error,
      helperText,
      onBlur,
      disabled = false,
      ...textFieldProps
    },
    ref,
  ) {
    const [touched, setTouched] = useState(false);
    const { blank, helperText: blankMessage } = useVisibleTextError(value, {
      touched,
      submitAttempted,
      initialFromServer,
    });

    // The callback is read through a ref so that an inline arrow function from
    // the caller does not re-fire the report on every render: the report is a
    // transition signal, and a form counting transitions must be able to trust
    // it.
    const onBlankChangeRef = useRef(onBlankChange);
    useEffect(() => {
      onBlankChangeRef.current = onBlankChange;
    }, [onBlankChange]);

    const lastReported = useRef<boolean | null>(null);
    useEffect(() => {
      if (lastReported.current === blank) return;
      lastReported.current = blank;
      onBlankChangeRef.current?.(blank);
    }, [blank]);

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setTouched(true);
        onBlur?.(event);
      },
      [onBlur],
    );

    // A disabled field says nothing: the user cannot act on it, and the save
    // gate belongs to the form (RULE-035 (c), the disabled state).
    const showBlankMessage = blankMessage !== null && !disabled;

    // The caller's own validation always WINS the message slot - a
    // required-field or uniqueness message is never hidden behind this one.
    // The invalid STATE is the union of the two, so a blank value can never
    // read back to a screen reader as valid just because the form also had
    // something to say.
    const displayHelperText = helperText !== undefined
      ? helperText
      : showBlankMessage
        ? blankMessage
        : undefined;
    const displayError = Boolean(error) || showBlankMessage;

    return (
      <TextField
        {...textFieldProps}
        ref={ref}
        value={value}
        disabled={disabled}
        error={displayError}
        helperText={displayHelperText}
        onBlur={handleBlur}
      />
    );
  },
);

export default VisibleTextField;
